import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useTranslation from '~/app/hooks/useTranslation';
import Icon from '~/app/common/Icon';
import Button from '~/app/common/Button';
import BottomPopup from '~/app/common/BottomPopup';
import Loader from '~/app/common/Loader';
import TextDialog from '~/app/common/TextDialog';
import useForm from '~/app/modules/form/useForm';
import useDidMountEffect from '~/app/hooks/useDidMountEffect';
import { useStoreActions, useStoreState } from '~/store';
import { notify } from '~/app/helpers/notify';
import { WorkOrderDetail } from '~/workorder/types';
import {
    EDIT_WORK_ORDER_ADMIN_FORM,
    EDIT_WORK_ORDER_ADMIN_FORM_FIELDS,
    EditWorkOrderAdministrativeFormValues,
    buildAdministrativeSavePayload,
    detailToAdministrativeFormValues,
    detailToEditFormValues,
    editFormValuesToAdministrative,
    validateAdministrativeEditForm,
} from '../adminFormHelper';
import { resolveOptionLabel } from '../helper';
import AdministrativeEditForm from './Form';
import classes from './style.module.scss';
import WorkTypeChangeDialog from '../WorkTypeChangeDialog';

export type EditPopupProps = {
    data: WorkOrderDetail;
    show: boolean;
    onClose: () => void;
};

type EditPopupInnerProps = Omit<EditPopupProps, 'show'>;

const EditPopupInner: React.FC<EditPopupInnerProps> = ({ data, onClose }) => {
    const { t } = useTranslation('workorder');
    const { t: tAPIResponseMessages } = useTranslation('apiResponseMessages');
    const { saveAdministrativeData, getWorkorderDepartments, getWorkOrderDetail } = useStoreActions(actions => actions.workOrder);
    const costCategories = useStoreState(state => state.issue.costCategories);
    const customers = useStoreState(state => state.issue.customers);
    const appDepartments = useStoreState(state => state.app.appData.departments);
    const workOrderDepartments = useStoreState(state => state.workOrder.departments ?? []);
    const fetchingDepartments = useStoreState(state => state.workOrder.fetchingDepartments);

    const initialAdministrative = useMemo(
        () => detailToAdministrativeFormValues(data),
        [data],
    );
    const { values, setValue, removeForm } = useForm<EditWorkOrderAdministrativeFormValues>({
        formName: EDIT_WORK_ORDER_ADMIN_FORM,
        fields: [...EDIT_WORK_ORDER_ADMIN_FORM_FIELDS],
        defaultValues: detailToEditFormValues(data),
    });

    const [saving, setSaving] = useState(false);
    const [workTypeDialogOpen, setWorkTypeDialogOpen] = useState(false);
    const [pendingWorkType, setPendingWorkType] = useState<number | null>(null);
    const [workTypeRevertValue, setWorkTypeRevertValue] = useState<number>(Number(data.workType ?? 0));
    const [pendingChangeAll, setPendingChangeAll] = useState<boolean | null>(null);
    const [workTypeFieldKey, setWorkTypeFieldKey] = useState(0);
    const [costCategoryFieldKey, setCostCategoryFieldKey] = useState(0);
    const [costCategoryBlockedOpen, setCostCategoryBlockedOpen] = useState(false);
    const suppressWorkTypePromptForRef = useRef<number | null>(null);

    const shouldSuppressWorkTypePrompt = useCallback((newWorkType: number): boolean => {
        const suppressed = suppressWorkTypePromptForRef.current;
        if (suppressed === null) {
            return false;
        }
        if (suppressed === newWorkType) {
            return true;
        }
        suppressWorkTypePromptForRef.current = null;
        return false;
    }, []);

    useEffect(() => {
        if (workOrderDepartments.length > 0) {
            return;
        }
        getWorkorderDepartments();
    }, [getWorkorderDepartments, workOrderDepartments.length]);

    useDidMountEffect(() => () => {
        removeForm();
    });

    const departmentLabel = useMemo(
        () => resolveOptionLabel(
            appDepartments?.map(d => ({ text: d.name, value: d.id })) ?? [],
            data.departmentId,
        ),
        [appDepartments, data.departmentId],
    );

    const applyWorkTypeToForm = useCallback((workType: number, remountSelect: boolean) => {
        setValue('workType', workType);
        if (remountSelect) {
            setWorkTypeFieldKey(key => key + 1);
        }
    }, [setValue]);

    const handleWorkTypeChangeAttempt = useCallback((newWorkType: number) => {
        setWorkTypeRevertValue(Number(values?.workType ?? 0));
        setPendingWorkType(newWorkType);
        setWorkTypeDialogOpen(true);
    }, [values?.workType]);

    const handleWorkTypeChangeWithoutPrompt = useCallback(() => {
        setPendingChangeAll(null);
    }, []);

    const closeWorkTypeDialog = useCallback(() => {
        setWorkTypeDialogOpen(false);
        setPendingWorkType(null);
    }, []);

    const handleWorkTypeKeep = useCallback(() => {
        if (pendingWorkType !== null) {
            suppressWorkTypePromptForRef.current = pendingWorkType;
            applyWorkTypeToForm(pendingWorkType, false);
            setPendingChangeAll(false);
        }
        closeWorkTypeDialog();
    }, [applyWorkTypeToForm, closeWorkTypeDialog, pendingWorkType]);

    const handleWorkTypeChangeAll = useCallback(() => {
        if (pendingWorkType !== null) {
            suppressWorkTypePromptForRef.current = pendingWorkType;
            applyWorkTypeToForm(pendingWorkType, false);
            setPendingChangeAll(true);
        }
        closeWorkTypeDialog();
    }, [applyWorkTypeToForm, closeWorkTypeDialog, pendingWorkType]);

    const handleWorkTypeCancel = useCallback(() => {
        suppressWorkTypePromptForRef.current = workTypeRevertValue;
        applyWorkTypeToForm(workTypeRevertValue, true);
        setPendingChangeAll(null);
        closeWorkTypeDialog();
    }, [applyWorkTypeToForm, closeWorkTypeDialog, workTypeRevertValue]);

    const handleCostCategoryBlocked = useCallback(() => {
        setCostCategoryFieldKey(key => key + 1);
        setCostCategoryBlockedOpen(true);
    }, []);

    const submitSave = useCallback(async () => {
        if (!values) {
            return;
        }

        const adminValues = editFormValuesToAdministrative({
            personnelId: Number(values.personnelId ?? 0),
            repairLocation: String(values.repairLocation ?? ''),
            workType: Number(values.workType ?? 0),
            costCategory: Number(values.costCategory ?? 0),
            chargedDepartmentId: Number(values.chargedDepartmentId ?? 0),
            customer: Number(values.customer ?? 0),
            project: Number(values.project ?? 0),
            sortingType: Number(values.sortingType ?? 0),
            invoiceAcceptanceUserId: Number(values.invoiceAcceptanceUserId ?? 0),
        });
        const validationError = validateAdministrativeEditForm(
            adminValues,
            data.status,
            costCategories,
            customers,
        );
        if (validationError) {
            notify(t(`error.${validationError}`), 'error');
            return;
        }

        const changeAllForPayload = pendingChangeAll === null ? undefined : pendingChangeAll;
        const payload = buildAdministrativeSavePayload(
            data,
            adminValues,
            changeAllForPayload,
        );

        setSaving(true);
        try {
            const response = await saveAdministrativeData(payload);
            if (response.success) {
                await getWorkOrderDetail({ id: Number(data.id), showLoading: false });
                notify(tAPIResponseMessages('changesSaved'), 'success');
                setPendingChangeAll(null);
                return;
            }
            notify(response.message || t('detail.error.administrativeUpdateFailed'), 'error');
        } catch {
            notify(t('detail.error.administrativeUpdateFailed'), 'error');
        } finally {
            setSaving(false);
        }
    }, [
        values,
        data,
        costCategories,
        customers,
        pendingChangeAll,
        saveAdministrativeData,
        getWorkOrderDetail,
        t,
        tAPIResponseMessages,
    ]);

    if (!values || fetchingDepartments) {
        return <Loader size="32" />;
    }

    return (
        <>
            <BottomPopup show onClose={onClose}>
                <div className={classes.popup}>
                    <div className={classes.popupHeader}>
                        <div>
                            <h3 className={classes.popupTitle}>{t('detail.editAdministrativeTitle')}</h3>
                            <p className={classes.popupSubtitle}>{t('detail.editAdministrativeSubtitle')}</p>
                        </div>
                        <button type="button" className={classes.closeBtn} onClick={onClose} aria-label={t('detail.closeEdit')}>
                            <Icon name="close" size={24} />
                        </button>
                    </div>

                    <div className={classes.scrollBody}>
                        <AdministrativeEditForm
                            data={data}
                            departmentLabel={departmentLabel}
                            initialAdministrative={initialAdministrative}
                            workTypeFieldKey={workTypeFieldKey}
                            costCategoryFieldKey={costCategoryFieldKey}
                            onWorkTypeChangeAttempt={handleWorkTypeChangeAttempt}
                            onWorkTypeChangeWithoutPrompt={handleWorkTypeChangeWithoutPrompt}
                            shouldSuppressWorkTypePrompt={shouldSuppressWorkTypePrompt}
                            onCostCategoryBlocked={handleCostCategoryBlocked}
                        />
                    </div>

                    <div className={classes.footer}>
                        <Button className={classes.cancelBtn} onClick={onClose} disabled={saving}>
                            {t('detail.cancelEdit')}
                        </Button>
                        <Button className={classes.saveBtn} onClick={() => submitSave()} loading={saving}>
                            {t('detail.saveAdministrative')}
                        </Button>
                    </div>
                </div>
            </BottomPopup>

            <WorkTypeChangeDialog
                isOpen={workTypeDialogOpen}
                onKeepExisting={handleWorkTypeKeep}
                onChangeAll={handleWorkTypeChangeAll}
                onCancel={handleWorkTypeCancel}
            />

            <TextDialog
                isOpen={costCategoryBlockedOpen}
                onClose={() => setCostCategoryBlockedOpen(false)}
                text={t('detail.costCategoryBlockedByMaterials')}
            />
        </>
    );
};

const EditPopup: React.FC<EditPopupProps> = ({ data, show, onClose }) => {
    if (!show) {
        return null;
    }

    return (
        <EditPopupInner
            key={data.id}
            data={data}
            onClose={onClose}
        />
    );
};

export default EditPopup;
